import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';
import LoadingOverlay from '../components/LoadingOverlay';

const HOST_API = process.env.REACT_APP_URL;

const formatMoney = (n) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n ?? 0);

const formatPeriodo = (p) => {
    if (!p) return '—';
    const label = new Date(p).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const ESTADO_COLORS = {
    VIGENTE:  { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    PAGADO:   { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    VENCIDO:  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    PENDIENTE: { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

const estadoBadge = (row) => {
    const estado = row.estado || '—';
    const s = ESTADO_COLORS[estado.trim().toUpperCase()] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {capitalize(estado)}
        </span>
    );
};

// Cuotas de plan de pago: "Pendiente" se muestra como "Vigente" (celeste), "Pagada" en verde.
const CUOTA_ESTADO_MAP = {
    PENDIENTE: { label: 'Vigente', bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    PAGADA:    { label: 'Pagada',  bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
};

const cuotaEstadoBadge = (row) => {
    const estado = (row.estado || '').trim();
    const s = CUOTA_ESTADO_MAP[estado.toUpperCase()];
    if (!s) {
        return (
            <span style={{
                background: '#f2f4f7', color: '#667085',
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {estado || '—'}
            </span>
        );
    }
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {s.label}
        </span>
    );
};

const EstadoCuentaAdmin = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const [ufs, setUfs] = useState([]);
    const [ufSel, setUfSel] = useState(null);
    const [periodo, setPeriodo] = useState(new Date());

    const [estadoCuenta, setEstadoCuenta] = useState([]);
    const [cuotas, setCuotas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data ?? []))
            .catch(() => {});
    }, [barrio?.id, administracionId]);

    const buscarEstadoCuenta = useCallback(async () => {
        if (!barrio?.id || !ufSel || !periodo) return;
        setLoading(true);
        setBuscado(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/estadoCuentas/GetEstadoCuentaPeriodo`, {
                params: {
                    administracionId,
                    barrioId: barrio.id,
                    unidadFuncionalId: ufSel,
                    periodo: formatDate(periodo),
                },
            });
            setEstadoCuenta(data?.historial ?? []);
            setCuotas(data?.cuotas ?? []);
        } catch {
            setEstadoCuenta([]);
            setCuotas([]);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo obtener el estado de cuenta', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, ufSel, periodo, administracionId]);

    return (
        <>
            <h4>Estado de Cuenta</h4>
            <br />
            <Toast ref={toast} />

            <div className="flex align-items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label style={lbl}>{t.UnidadFuncional} *</label>
                    <Dropdown
                        value={ufSel}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSel(e.value)}
                        placeholder={`Seleccionar ${t.UF}`}
                        filter
                        className="p-inputtext-sm"
                        style={{ width: 220 }}
                    />
                </div>
                <div>
                    <label style={lbl}>Período *</label>
                    <Calendar
                        value={periodo}
                        onChange={(e) => setPeriodo(e.value)}
                        view="month"
                        dateFormat="mm/yy"
                        placeholder="mm/aaaa"
                        className="p-inputtext-sm"
                        style={{ width: 140 }}
                    />
                </div>
                <Button label="Buscar" icon="pi pi-search" className="p-button-sm"
                    onClick={buscarEstadoCuenta} disabled={!ufSel || !periodo} />
            </div>

            <LoadingOverlay visible={loading} />
            {!loading && buscado && (
                <div className="grid">
                    <div className="col-12">
                        <h5>Expensas</h5>
                        <DataTable value={estadoCuenta} showGridlines responsiveLayout="scroll"
                            emptyMessage={`No hay estado de cuenta registrado para esta ${t.unidadFuncional} en el período seleccionado`}
                            className="p-datatable-gridlines bg-white" style={{ fontSize: '0.88rem' }}>
                            <Column field="periodo" header="Período" sortable body={(r) => formatPeriodo(r.periodo)} style={{ minWidth: 150 }} />
                            <Column field="saldoDeudor" header="Saldo Deudor" sortable body={(r) => formatMoney(r.saldoDeudor)} style={{ minWidth: 130 }} />
                            <Column field="intereses" header="Intereses" sortable body={(r) => formatMoney(r.intereses)} style={{ minWidth: 120 }} />
                            <Column field="expensaMes" header="Expensa del mes" sortable body={(r) => formatMoney(r.expensaMes)} style={{ minWidth: 140 }} />
                            <Column field="recargoSegundoVencimiento" header="Recargo 2do Vencimiento" sortable body={(r) => formatMoney(r.recargoSegundoVencimiento)} style={{ minWidth: 150 }} />
                            <Column field="descuentos" header="Descuentos" sortable body={(r) => formatMoney(r.descuentos)} style={{ minWidth: 130 }} />
                            <Column field="pagosRealizados" header="Pagos realizados" sortable body={(r) => formatMoney(r.pagosRealizados)} style={{ minWidth: 150 }} />
                            <Column
                                field="saldoFinal"
                                header="Saldo Final"
                                sortable
                                body={(r) => (
                                    <span style={{ fontWeight: 700, color: (r.saldoFinal ?? 0) <= 0 ? '#12b76a' : '#f04438' }}>
                                        {formatMoney(r.saldoFinal)}
                                    </span>
                                )}
                                style={{ minWidth: 130 }}
                            />
                            <Column field="estado" header="Estado" body={estadoBadge} style={{ minWidth: 110 }} />
                        </DataTable>
                    </div>

                    <div className="col-12">
                        <h5>Cuotas de plan de pago</h5>
                        <DataTable value={cuotas} showGridlines responsiveLayout="scroll"
                            emptyMessage={`No hay cuotas de plan de pago registradas para esta ${t.unidadFuncional} en el período seleccionado`}
                            className="p-datatable-gridlines bg-white" style={{ fontSize: '0.88rem' }}>
                            <Column field="periodo" header="Período" sortable body={(r) => formatPeriodo(r.periodo)} style={{ minWidth: 150 }} />
                            <Column field="cuota" header="Cuota" sortable style={{ minWidth: 90 }} />
                            <Column field="concepto" header="Concepto" body={(r) => r.concepto || '—'} style={{ minWidth: 160 }} />
                            <Column field="importe" header="Importe" sortable body={(r) => formatMoney(r.importe)} style={{ minWidth: 130 }} />
                            <Column field="pagado" header="Pagado" sortable body={(r) => formatMoney(r.pagado)} style={{ minWidth: 130 }} />
                            <Column field="saldo" header="Saldo" sortable body={(r) => formatMoney(r.saldo)} style={{ minWidth: 130 }} />
                            <Column field="estado" header="Estado" body={cuotaEstadoBadge} style={{ minWidth: 110 }} />
                        </DataTable>
                    </div>
                </div>
            )}
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default EstadoCuentaAdmin;
